<script setup lang="ts">
import EmojiListGrid, { DEFAULT_COLUMNS } from '@@/vuecomponents/EmojiListGrid.vue'
import EmojiListSingleRow from '@@/vuecomponents/EmojiListSingleRow.vue'
import { useVirtualKeyboardHeight } from '@@/vuecomposables/virtual_keyboard_height'
import type { Emoji as EmojiType } from '@padlet/arvo'
import { computed, nextTick, shallowRef, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    isLoading: boolean
    emojis: EmojiType[]
    darkMode: boolean
    /**
     * If layout is `single-row`, `columns` is ignored.
     */
    layout?: 'grid' | 'single-row'
    columns?: number
    selectedEmojiUrl?: string
    xCheckmark?: boolean
    iconSize?: number
    hoveredIconSize?: number
    checkmarkSize?: number
    groupByCategory?: boolean
    xThinScrollbar?: boolean
    draggable?: boolean
  }>(),
  {
    layout: 'grid',
    columns: DEFAULT_COLUMNS,
    selectedEmojiUrl: '',
    xCheckmark: true,
    iconSize: 40,
    hoveredIconSize: undefined,
    checkmarkSize: 14,
    groupByCategory: false,
    xThinScrollbar: false,
    draggable: false,
  },
)

const emit = defineEmits<{
  (e: 'pick', url: string): void
  (e: 'category-visible', categoryLabel: string): void
  (e: 'emoji-hover', event: MouseEvent | FocusEvent, emoji: EmojiType): void
  (e: 'emoji-unhover'): void
}>()

const { awaitVirtualKeyboardHidden } = useVirtualKeyboardHeight()

const gridRef = shallowRef<InstanceType<typeof EmojiListGrid> | null>(null)
const singleRowRef = shallowRef<InstanceType<typeof EmojiListSingleRow> | null>(null)

const isScrolled = computed(() => {
  if (props.layout === 'grid') {
    return gridRef.value?.isScrolled ?? false
  } else {
    return singleRowRef.value?.isScrolled ?? false
  }
})

// Track when user deliberately switches categories to prevent position preservation interference
let pendingTargetCategoryLabel: string | null = null

const scrollToCategory = async (categoryLabel: string) => {
  // Mark that user deliberately switched categories
  pendingTargetCategoryLabel = categoryLabel

  // Because this method can be called when the user switches layouts, for example:
  // 1. In single row layout, virtual keyboard is shown
  // 2. Clicks on a category to switch to it
  // 3. Click handler is triggered, `scrollToCategory` (this method) is called
  // 4. Virtual keyboard is now hidden
  // We can't immediately scroll to the category. We're only at step 3 now and
  // the layout hasn't switched yet. The **key** here is we have to wait until
  // after step 4. Hence why `awaitVirtualKeyboardHidden` is used.
  await awaitVirtualKeyboardHidden()
  if (props.layout === 'grid') {
    gridRef.value?.scrollToCategory(categoryLabel)
  }
}

/**
 * Scrolls to the specified emoji by its flat array index
 */
const scrollToEmoji = (emojiIndex: number) => {
  if (props.layout === 'grid') {
    gridRef.value?.scrollToEmoji(emojiIndex)
  } else {
    singleRowRef.value?.scrollToEmoji(emojiIndex)
  }
}

/**
 * Watch for layout changes and preserve scroll position
 */
watch(
  () => props.layout,
  async (newLayout, oldLayout) => {
    if (oldLayout && oldLayout !== newLayout) {
      // Check if user deliberately switched categories - if so, don't interfere with position preservation
      if (pendingTargetCategoryLabel) {
        pendingTargetCategoryLabel = null
        return
      }

      // Capture current visible emoji index before layout switch.
      // Old component is still mounted and functional at this point thanks to `flush: 'pre'`.
      const currentEmojiIndex =
        oldLayout === 'grid' ? gridRef.value?.visibleEmojiIndex : singleRowRef.value?.visibleEmojiIndex
      if (currentEmojiIndex == null || currentEmojiIndex < 0) return

      // Wait for new layout component to mount, then restore position
      await nextTick()
      scrollToEmoji(currentEmojiIndex)
    }
  },
  { flush: 'pre' },
)

const handleCategoryVisible = (categoryLabel: string) => {
  // Check if this matches our target category from a deliberate scroll.
  // If yes, reset the pending target category label because we've successfully scrolled to it.
  if (pendingTargetCategoryLabel != null && pendingTargetCategoryLabel === categoryLabel) {
    pendingTargetCategoryLabel = null
  }
  emit('category-visible', categoryLabel)
}

defineExpose({
  isScrolled,
  scrollToCategory,
  scrollToEmoji,
})
</script>

<script lang="ts">
export { DEFAULT_COLUMNS } from '@@/vuecomponents/EmojiListGrid.vue'
</script>

<template>
  <EmojiListGrid
    v-if="layout === 'grid'"
    ref="gridRef"
    :is-loading="isLoading"
    :emojis="emojis"
    :dark-mode="darkMode"
    :columns="columns"
    :selected-emoji-url="selectedEmojiUrl"
    :icon-size="iconSize"
    :hovered-icon-size="hoveredIconSize"
    :checkmark-size="checkmarkSize"
    :group-by-category="groupByCategory"
    :x-thin-scrollbar="xThinScrollbar"
    :draggable="draggable"
    :x-checkmark="xCheckmark"
    @pick="$emit('pick', $event)"
    @category-visible="handleCategoryVisible"
    @emoji-hover="(event, emoji) => $emit('emoji-hover', event, emoji)"
    @emoji-unhover="$emit('emoji-unhover')"
  />
  <EmojiListSingleRow
    v-else
    ref="singleRowRef"
    :is-loading="isLoading"
    :emojis="emojis"
    :dark-mode="darkMode"
    :icon-size="iconSize"
    :hovered-icon-size="hoveredIconSize"
    :draggable="draggable"
    @pick="$emit('pick', $event)"
    @category-visible="handleCategoryVisible"
  />
</template>
